'use strict';
/**
 * Module dependencies.
 */

var utilities = require('./utilities');

var detectIE = utilities.isIE;
/**
 * Constants.
 */

var HTML_TAG_NAME = 'html';
var BODY_TAG_NAME = 'body';
var HEAD_TAG_NAME = 'head';
var FIRST_TAG_REGEX = /<([a-zA-Z]+[0-9]?)/; // e.g., <h1>

var HEAD_REGEX = /<\/head>/i;
var BODY_REGEX = /<\/body>/i; // http://www.w3.org/TR/html/syntax.html#void-elements

var VOID_ELEMENTS_REGEX = /<(area|base|br|col|embed|hr|img|input|keygen|link|menuitem|meta|param|source|track|wbr)(.*?)\/?>/gi; // browser support

var isIE = detectIE();
var isIE9 = detectIE(9);
/**
 * DOMParser (performance: slow).
 *
 * https://developer.mozilla.org/docs/Web/API/DOMParser#Parsing_an_SVG_or_HTML_document
 */

var parseFromString;

if (typeof window.DOMParser === 'function') {
  var domParser = new window.DOMParser(); // IE9 does not support 'text/html' MIME type
  // https://msdn.microsoft.com/en-us/library/ff975278(v=vs.85).aspx

  var MIME_TYPE = isIE9 ? 'text/xml' : 'text/html';
  /**
   * Creates an HTML document using `DOMParser.parseFromString`.
   *
   * @param  {String} html      - The HTML string.
   * @param  {String} [tagName] - The element to render the HTML (with 'body' as fallback).
   * @return {HTMLDocument}
   */

  parseFromString = function domStringParser(html, tagName) {
    if (tagName) {
      html = ['<', tagName, '>', html, '</', tagName, '>'].join('');
    } // because IE9 only supports MIME type 'text/xml', void elements need to be self-closed


    if (isIE9) {
      html = html.replace(VOID_ELEMENTS_REGEX, '<$1$2$3/>');
    }

    return domParser.parseFromString(html, MIME_TYPE);
  };
}
/**
 * DOMImplementation (performance: fair).
 *
 * https://developer.mozilla.org/docs/Web/API/DOMImplementation/createHTMLDocument
 */


var parseFromDocument;

if (typeof document.implementation === 'object') {
  // title parameter is required in IE
  // https://msdn.microsoft.com/en-us/library/ff975457(v=vs.85).aspx
  var doc = document.implementation.createHTMLDocument(isIE ? 'HTML_DOM_PARSER_TITLE' : undefined);
  /**
   * Use HTML document created by `document.implementation.createHTMLDocument`.
   *
   * @param  {String} html      - The HTML string.
   * @param  {String} [tagName] - The element to render the HTML (with 'body' as fallback).
   * @return {HTMLDocument}
   */

  parseFromDocument = function createHTMLDocument(html, tagName) {
    if (tagName) {
      doc.documentElement.getElementsByTagName(tagName)[0].innerHTML = html;
      return doc;
    }

    try {
      doc.documentElement.innerHTML = html;
      return doc; // fallback when certain elements in `documentElement` are read-only (IE9)
    } catch (err) {
      if (parseFromString) return parseFromString(html);
    }
  };
}
/**
 * Template (performance: fast).
 *
 * https://developer.mozilla.org/docs/Web/HTML/Element/template
 */


var parseFromTemplate;
var template = document.createElement('template');

if (template.content) {
  /**
   * Uses a template element (content fragment) to parse HTML.
   *
   * @param  {String} html - The HTML string.
   * @return {NodeList}
   */
  parseFromTemplate = function templateParser(html) {
    template.innerHTML = html;
    return template.content.childNodes;
  };
}
/** Fallback document parser. */


var parseWithFallback = parseFromDocument || parseFromString;
/**
 * Parses HTML string to DOM nodes.
 *
 * @param  {String} html      - The HTML string.
 * @param  {String} [tagName] - The tag name.
 * @return {NodeList|Array}
 */

module.exports = function domparser(html) {
  // try to match first tag
  var tagName;
  var match = html.match(FIRST_TAG_REGEX);

  if (match && match[1]) {
    tagName = match[1].toLowerCase();
  }

  var doc;
  var element;
  var elements;

  switch (tagName) {
    case HTML_TAG_NAME:
      if (parseFromString) {
        doc = parseFromString(html); // the created document may come with filler head/body elements,
        // so ake sure to remove them if they don't actually exist

        if (!HEAD_REGEX.test(html)) {
          element = doc.getElementsByTagName(HEAD_TAG_NAME)[0];
          if (element) element.parentNode.removeChild(element);
        }

        if (!BODY_REGEX.test(html)) {
          element = doc.getElementsByTagName(BODY_TAG_NAME)[0];
          if (element) element.parentNode.removeChild(element);
        }

        return doc.getElementsByTagName(HTML_TAG_NAME);
      }

      break;

    case HEAD_TAG_NAME:
      if (parseWithFallback) {
        elements = parseWithFallback(html).getElementsByTagName(HEAD_TAG_NAME); // account for possibility of sibling

        if (BODY_REGEX.test(html)) {
          return elements[0].parentNode.childNodes;
        }

        return elements;
      }

      break;

    case BODY_TAG_NAME:
      if (parseWithFallback) {
        elements = parseWithFallback(html).getElementsByTagName(BODY_TAG_NAME); // account for possibility of sibling (return both body and head)

        if (HEAD_REGEX.test(html)) {
          return elements[0].parentNode.childNodes;
        }

        return elements;
      }

      break;
    // low-level tag or text

    default:
      if (parseFromTemplate) return parseFromTemplate(html);

      if (parseWithFallback) {
        return parseWithFallback(html, BODY_TAG_NAME).getElementsByTagName(BODY_TAG_NAME)[0].childNodes;
      }

      break;
  }

  return [];
};